import type { ValueTransformer } from 'typeorm';
import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { SensorSubmissionEntity } from './sensor-submission.entity';
import { SensorEntity } from './sensor.entity';

/**
 * The mysql2 driver returns DECIMAL columns as strings ("-2.00"), while the
 * properties below are typed as numbers. Every consumer therefore held a
 * string typed as a number, and the Location sensor card crashed on
 * `.toFixed` the first time it received a real reading (PD-16194). Parsing
 * here makes the declared type true for every reader.
 */
const decimalAsNumber: ValueTransformer = {
	from: (value: string | number | null): number | null =>
		value == null ? null : Number(value),
	to: (value: number | null | undefined): number | null | undefined => value,
};

@Index('sensor_reading_sensor_idx', ['sensorId'])
// Serves the ingestion and history queries that read a sensor's readings
// newest-first. Created by database sync so a new instance gets it too;
// it replaces the obsolete AddSensorIngestionIndexes migration (PD-16092).
@Index('sensor_reading_sensor_time_idx', ['sensorId', 'createdDate'])
@Entity('sensor_reading', {}, { auditable: false })
export class SensorReadingEntity extends BaseEntity {
	@Column('int', { name: 'fk_sensor__id', unsigned: true })
	public sensorId!: number;

	// --- Legacy columns (kept for backward compatibility, will be dropped after staging deploy) ---

	@Column('decimal', {
		name: 'fill_level',
		precision: 5,
		scale: 2,
		nullable: true,
		transformer: decimalAsNumber,
	})
	public fillLevel!: number | null;

	@Column('decimal', {
		name: 'temperature',
		precision: 5,
		scale: 2,
		nullable: true,
		transformer: decimalAsNumber,
	})
	public temperature!: number | null;

	@Column('decimal', {
		name: 'humidity',
		precision: 5,
		scale: 2,
		nullable: true,
		transformer: decimalAsNumber,
	})
	public humidity!: number | null;

	@Column('int', { name: 'signal_strength', nullable: true })
	public signalStrength!: number | null;

	@Column('decimal', {
		name: 'battery_level',
		precision: 5,
		scale: 2,
		nullable: true,
		transformer: decimalAsNumber,
	})
	public batteryLevel!: number | null;

	// --- Per-module fields (from sensor.modules[]) ---

	@Column('int', { name: 'distance', nullable: true })
	public distance!: number | null;

	@Column('decimal', {
		name: 'module_temperature',
		precision: 5,
		scale: 2,
		nullable: true,
		transformer: decimalAsNumber,
	})
	public moduleTemperature!: number | null;

	@Column('smallint', { name: 'module_signal_strength', nullable: true })
	public moduleSignalStrength!: number | null;

	// --- Per-device fields (from status) ---

	@Column('smallint', {
		name: 'battery_voltage',
		unsigned: true,
		nullable: true,
	})
	public batteryVoltage!: number | null;

	@Column('decimal', {
		name: 'device_temperature',
		precision: 5,
		scale: 2,
		nullable: true,
		transformer: decimalAsNumber,
	})
	public deviceTemperature!: number | null;

	@Column('int', { name: 'cellular_signal_strength', nullable: true })
	public cellularSignalStrength!: number | null;

	@Column('tinyint', { name: 'cellular_signal_quality', nullable: true })
	public cellularSignalQuality!: number | null;

	@Column('tinyint', { name: 'power_source', nullable: true })
	public powerSource!: number | null;

	// --- Submission FK ---

	@Column('int', {
		name: 'fk_sensor_submission__id',
		unsigned: true,
		nullable: true,
	})
	public submissionId!: number | null;

	// --- Relations ---

	@ManyToOne(() => SensorEntity)
	@JoinColumn({ name: 'fk_sensor__id' })
	public fkSensor!: SensorEntity;

	@ManyToOne(() => SensorSubmissionEntity)
	@JoinColumn({ name: 'fk_sensor_submission__id' })
	public fkSensorSubmission!: SensorSubmissionEntity;
}
